import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  useCreateStandaloneCollaborationMutation,
  useInviteBrandMutation,
} from '@/redux/collaborationsApi'
import { Plus, Loader2, Send } from 'lucide-react'
import { toast } from '@/components/ui/sonner'
import { useAuth } from '@/hooks/useAuth'
import { INPUT_CLASSES } from '@/lib/theme'
import { DeliverableBuilder, createDefaultDeliverable } from '@adthlete/shared-frontend'
import type { DeliverableItem } from '@adthlete/shared-frontend'

const PLATFORMS = ['Instagram', 'TikTok', 'YouTube', 'Facebook']

interface CreateCollaborationModalProps {
  initialStep?: 'create' | 'contact' | 'message'
  initialCollaborationId?: string | null
  trigger?: React.ReactNode
}

export function CreateCollaborationModal({
  initialStep = 'create',
  initialCollaborationId = null,
  trigger,
}: CreateCollaborationModalProps) {
  const [open, setOpen] = useState(false)
  const [step, setStep] = useState<'create' | 'contact' | 'message'>(initialStep)
  const [collaborationId, setCollaborationId] = useState<string | null>(initialCollaborationId)

  // Create Collaboration State
  const [title, setTitle] = useState('')
  const [platforms, setPlatforms] = useState<string[]>([])
  const [paymentAmount, setPaymentAmount] = useState('')
  const [deliverables, setDeliverables] = useState<DeliverableItem[]>([createDefaultDeliverable()])
  const [dueDate, setDueDate] = useState('')

  // Invite State
  const [email, setEmail] = useState('')
  const [brandName, setBrandName] = useState('')
  const [inviteMessage, setInviteMessage] = useState('')

  const { athlete } = useAuth()

  const [createCollaboration, { isLoading: isCreating }] =
    useCreateStandaloneCollaborationMutation()
  const [inviteBrand, { isLoading: isInviting }] = useInviteBrandMutation()

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()

    const cleanedDeliverables = deliverables.filter((item) => item.platform && item.format)

    if (!title.trim() || !paymentAmount || !dueDate) {
      toast.error('Please fill in all required fields')
      return
    }

    if (cleanedDeliverables.length === 0) {
      toast.error('Add at least one deliverable')
      return
    }

    try {
      const result = await createCollaboration({
        title: title.trim(),
        platform: platforms.length > 0 ? platforms : undefined,
        paymentAmount: parseFloat(paymentAmount),
        structuredDeliverables: cleanedDeliverables,
        dueDate,
      }).unwrap()

      setCollaborationId(result.id)
      setStep('contact')
      toast.success('Collaboration created! Now invite the brand.')
    } catch (error) {
      console.error('Failed to create collaboration:', error)
      toast.error('Failed to create collaboration')
    }
  }

  const handleContinueToMessage = (e: React.FormEvent) => {
    e.preventDefault()

    if (!email) {
      toast.error('Please enter an email address')
      return
    }

    const athleteName = athlete?.name || '[Your Name]'
    const greeting = brandName.trim() ? `Hi ${brandName.trim()},` : 'Hi,'
    setInviteMessage(
      `${greeting}\n\nI've set up our collaboration in Athios to centralize deliverables, timelines, and communication.\n\nPlease join using the link below so we can manage the project together and move forward efficiently.\n\nExcited to get started.\n\nBest,\n${athleteName}`,
    )

    setStep('message')
  }

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!collaborationId) return

    try {
      await inviteBrand({
        campaignId: collaborationId,
        email,
        brandName: brandName.trim() || undefined,
        message: inviteMessage,
      }).unwrap()

      toast.success('Invitation sent successfully!')
      setOpen(false)
      resetForm()
    } catch (error) {
      console.error('Failed to send invitation:', error)
      toast.error('Failed to send invitation')
    }
  }

  const resetForm = () => {
    setStep(initialStep)
    setCollaborationId(initialCollaborationId)
    setTitle('')
    setPlatforms([])
    setPaymentAmount('')
    setDeliverables([createDefaultDeliverable()])
    setDueDate('')
    setEmail('')
    setBrandName('')
    setInviteMessage('')
  }

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    if (!newOpen) {
      // Small delay to allow animation to finish before resetting
      setTimeout(resetForm, 300)
    }
  }

  const dialogTitle = {
    create: 'Create New Collaboration',
    contact: 'Invite Brand',
    message: 'Invitation Message',
  }[step]

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="lime">
            <Plus className="mr-2 h-4 w-4" />
            Create Collaboration
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[640px] bg-[#1a1a1a] text-white border-white/10">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-white">{dialogTitle}</DialogTitle>
        </DialogHeader>

        {step === 'create' && (
          <form onSubmit={handleCreate} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="title">Collaboration Title</Label>
              <Input
                id="title"
                type="text"
                placeholder="e.g. Summer Collab 2026"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={INPUT_CLASSES.dark}
                required
              />
              <p className="text-xs text-gray-500">
                The name you'll use to identify this collaboration.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Platform</Label>
              <div className="flex flex-wrap gap-2">
                {PLATFORMS.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() =>
                      setPlatforms((prev) =>
                        prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p],
                      )
                    }
                    className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                      platforms.includes(p)
                        ? 'bg-[#C0FD72] text-black border-[#C0FD72]'
                        : 'bg-white/5 text-gray-300 border-white/10 hover:border-white/30'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-500">Where you'll be posting the content.</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="paymentAmount">Your Rate ($)</Label>
              <Input
                id="paymentAmount"
                type="number"
                placeholder="e.g. 500"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                className={INPUT_CLASSES.dark}
                required
              />
              <p className="text-xs text-gray-500">
                How much you're charging the brand for this collaboration.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Deliverables</Label>
              <DeliverableBuilder
                value={deliverables}
                onChange={setDeliverables}
                hideDueDate
                hideSameAs
              />
              <p className="text-xs text-gray-500">
                What content you'll create. This is included in the invitation email sent to the
                brand.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="dueDate">Content Due Date</Label>
              <Input
                id="dueDate"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className={INPUT_CLASSES.dark}
                required
              />
              <p className="text-xs text-gray-500">
                The date you commit to having the content ready for the brand.
              </p>
            </div>

            <div className="pt-4 flex justify-end">
              <Button type="submit" variant="lime" disabled={isCreating}>
                {isCreating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Next: Invite Brand'
                )}
              </Button>
            </div>
          </form>
        )}

        {step === 'contact' && (
          <form onSubmit={handleContinueToMessage} className="space-y-4 mt-4">
            <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-md">
              <p className="text-sm text-blue-200">
                Collaboration details created. Send an email invitation to the person you are
                working with at the brand so you can manage the project together on our platform.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="brandName">Brand Name (Optional)</Label>
              <Input
                id="brandName"
                type="text"
                placeholder="Nike"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                className={INPUT_CLASSES.dark}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Brand Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="marketer@brand.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={INPUT_CLASSES.dark}
                required
              />
            </div>

            <div className="pt-4 flex justify-between">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setOpen(false)}
                className="text-gray-400"
              >
                Cancel
              </Button>
              <Button type="submit" variant="lime">
                Continue
              </Button>
            </div>
          </form>
        )}

        {step === 'message' && (
          <form onSubmit={handleInvite} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="inviteMessage">Message (Optional)</Label>
              <Textarea
                id="inviteMessage"
                value={inviteMessage}
                onChange={(e) => setInviteMessage(e.target.value)}
                className="bg-white/5 border-white/10 text-white min-h-[240px]"
              />
            </div>

            <div className="pt-4 flex justify-between">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setStep('contact')}
                className="text-gray-400"
              >
                Back
              </Button>
              <Button type="submit" variant="lime" disabled={isInviting}>
                {isInviting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Send Invitation
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
